const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_THEME_API_URL || 'http://localhost:8000'

function getAuthHeaders(): Record<string, string> {
  const authKey = 'impersonating-auth-token'
  const authData = localStorage.getItem(authKey)
  let token: string | null = null
  if (authData) {
    try {
      const auth = JSON.parse(authData)
      token = auth.api_token
    } catch {
      // ignore
    }
  }
  if (!token) {
    token =
      localStorage.getItem('access_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token')
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export interface PlannerStatusResponse {
  has_integration: boolean
  plan_id?: string
  /** Vínculos plano ↔ projeto; quando há ao menos um, a integração está concluída. */
  links?: { plan_id: string; project_id: number }[]
}

export interface PlannerConfigLink {
  id: number | null
  plan_id: string
  project_id: number
  project_name?: string
}

export interface PlannerConfigResponse {
  config: {
    tenant_id: string | null
    client_id: string | null
    last_sync_at: string | null
    links: PlannerConfigLink[]
  } | null
}

export interface PlannerInitResponse {
  success: boolean
  message: string
}

export interface PlannerTaskPreview {
  id: string
  title: string
  percent_complete: number
  due_date_time: string | null
  action: 'create' | 'update'
  taskrush_task_id?: number
}

export interface PlannerSyncResult {
  synced: number
  errors: string[]
  message: string
  dry_run?: boolean
  tasks?: PlannerTaskPreview[]
}

class PlannerApiService {
  async getStatus(): Promise<PlannerStatusResponse> {
    const response = await fetch(`${API_BASE_URL}/api/planner/status`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })
    if (!response.ok) throw new Error('Erro ao carregar status do Planner')
    return response.json()
  }

  async init(params: {
    tenant_id: string
    client_id: string
    client_secret: string
  }): Promise<PlannerInitResponse> {
    const body = {
      tenant_id: params.tenant_id.trim(),
      client_id: params.client_id.trim(),
      client_secret: params.client_secret.trim(),
    }
    const response = await fetch(`${API_BASE_URL}/api/planner/init`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(json?.message || 'Não foi possível iniciar a integração')
    }
    return json
  }

  async getConfig(): Promise<PlannerConfigResponse> {
    const response = await fetch(`${API_BASE_URL}/api/planner/config`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })
    if (!response.ok) throw new Error('Erro ao carregar configuração do Planner')
    return response.json()
  }

  async updateConfig(data: {
    tenant_id?: string
    client_id?: string
    client_secret?: string
    group_id?: string
    links: { plan_id: string; project_id: number }[]
  }): Promise<{message: string}> {
    const response = await fetch(`${API_BASE_URL}/api/planner/config`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(json?.message || 'Erro ao salvar configuração')
    }
    return json
  }

  /**
   * Sincroniza tarefas do Planner para o TaskRush.
   * @param dryRun Se true, apenas lista sem persistir.
   * @param planId Se informado com projectId, sincroniza apenas esse vínculo.
   * @param projectId Se informado com planId, sincroniza apenas esse vínculo.
   */
  async sync(dryRun = false, planId?: string, projectId?: number): Promise<PlannerSyncResult> {
    const params = new URLSearchParams()
    if (dryRun) params.set('dry_run', '1')
    if (planId != null && planId !== '') params.set('plan_id', planId)
    if (projectId != null) params.set('project_id', String(projectId))
    const qs = params.toString()
    const url = `${API_BASE_URL}/api/planner/sync${qs ? `?${qs}` : ''}`
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    if (!response.ok) throw new Error('Erro ao sincronizar')
    return response.json()
  }

  /**
   * Desvincula um plano de um projeto.
   * @param linkId ID do vínculo (planner_plan_project.id)
   * @param action 'keep_tasks' = manter tarefas desvinculadas; 'remove_tasks' = remover todas as tarefas do plano no projeto
   */
  async unlink(linkId: number, action: 'keep_tasks' | 'remove_tasks'): Promise<{message: string}> {
    const response = await fetch(`${API_BASE_URL}/api/planner/unlink`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ link_id: linkId, action }),
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(json?.message || 'Erro ao desvincular')
    }
    return json
  }

  /** Lista projetos da instância (primeira página, para select inicial). */
  async getProjects(limit = 20): Promise<{id: number; name: string}[]> {
    return this.getProjectsPage(1, limit, '')
  }

  /**
   * Lista uma página de projetos, com busca opcional.
   * @param page Página (1-based)
   * @param perPage Itens por página (ex.: 20)
   * @param search Termo para filtrar por nome do projeto ou do cliente (opcional)
   */
  async getProjectsPage(page: number, perPage: number, search: string): Promise<{id: number; name: string}[]> {
    const params = new URLSearchParams()
    params.set('per_page', String(perPage))
    params.set('page', String(page))
    if (search && search.trim() !== '') params.set('search', search.trim())
    const url = `${API_BASE_URL}/api/projetos?${params.toString()}`
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() })
    if (!response.ok) return []
    const json = await response.json().catch(() => ({}))
    const inner = json?.data ?? {}
    const list = Array.isArray(inner) ? inner : inner?.data ?? []
    return (list || []).map((p: {id: number; name: string}) => ({
      id: p.id,
      name: p.name || String(p.id),
    }))
  }
}

export const plannerApiService = new PlannerApiService()
